import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { TeamCompaniesService } from '../../../services/teamcompanies/teamcompanies.service';
import { UsersService } from '../../../services/users/users.service';
import { SpinnerService } from '../../../services/spinner/spinner.service';
import { TeamsService } from '../../../services/teams/teams.service';
import {
  PAYCOR_PROVIDER_SLUG,
  PayrollProviderConnectionResponse,
  PayrollProviderConnectionHealthCheck,
  PayrollService,
  UKG_READY_PROVIDER_SLUG,
  UkgDeductionCode,
} from '../../../services/payroll/payroll.service';
import { UiAlertService } from '../../../services/ui-alert/ui-alert.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackBarComponent } from '../../snackbar/snackbar.component';
import {
  PayrollConnectionDialogComponent,
  PayrollConnectionDialogData,
  PayrollConnectionDialogResult,
} from '../payroll-connection-dialog/payroll-connection-dialog.component';
import { Subscription } from 'rxjs';

interface PayrollConnectionRow {
  company: any;
  providerConnection: PayrollProviderConnectionResponse | null;
  providerSlug: string;
  name: string;
  type: string;
  systemId: string;
  status: string;
  healthTooltip?: string;
  connectedOn: string;
  lastReadAt?: string | null;
  canManageSetup: boolean;
  setupUnavailableReason: string;
  isRefreshingHealth?: boolean;
}

interface BankConnectionRow {
  company: any;
  teamIds: string[];
  teamNames: string[];
  methodKey: string;
  name: string;
  bank: string;
  account: string;
  fundingSource: string;
  status: string;
  connectedOn: string;
}

interface DeductionCodeInspectionRow {
  code: string;
  name: string;
  description: string;
  status: string;
}

interface PayrollConnectionValidationResult {
  isConnected: boolean;
  lastReadAt?: string | null;
  tooltip?: string;
}

const COMPANY_PROVISIONING_REQUIRED_MESSAGE = 'A Wallit administrator must provision the company and assign your Payroll Admin access before you can configure a connection.';

@Component({
  selector: 'app-payroll-connections',
  templateUrl: './payroll-connections.component.html',
  styleUrls: ['./payroll-connections.component.scss'],
})
export class PayrollConnectionsComponent implements OnInit, OnDestroy {
  payrollConnections: PayrollConnectionRow[] = [];
  bankConnections: BankConnectionRow[] = [];
  deductionCodeInspectionRows: DeductionCodeInspectionRow[] = [];
  deductionCodeInspectionProviderName = '';
  deductionCodeInspectionError = '';
  userCompanies: any[] = [];
  primaryCompany?: any;

  public ukgCompany?: any;
  private parentId = '';
  public isLoading = true;
  public isLoadingBankConnections = false;
  public isLoadingDeductionCodes = false;
  public isDeductionCodeInspectionExpanded = false;
  private userTeams: any[] = [];
  private handledOpenBankDialogFromQuery = false;
  private meSubscription?: Subscription;
  private reloadSequence = 0;

  constructor(
    private dialog: MatDialog,
    private spinnerService: SpinnerService,
    private teamCompaniesService: TeamCompaniesService,
    private usersService: UsersService,
    private teamsService: TeamsService,
    private payrollService: PayrollService,
    private uiAlertService: UiAlertService,
    private snackBar: MatSnackBar,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  async ngOnInit(): Promise<void> {
    this.spinnerService.show('Loading...');
    this.meSubscription = this.usersService.meSubscribe(async (me: any) => {
      if (me) {
        this.ukgCompany = await this.teamCompaniesService.getCompanyByNameAndType('UKG', 'agency');
        this.parentId = this.ukgCompany?.id ?? this.parentId;
        await this.reloadItems();
        this.maybeOpenBankDialogFromQuery();
      }
    });
  }

  get hasPayrollConnectionLimitReached(): boolean {
    return this.payrollConnections.length > 0;
  }

  get addPayrollConnectionTooltip(): string {
    return this.hasPayrollConnectionLimitReached
      ? 'Only one payroll connection is supported right now. Edit or remove the current connection before adding another.'
      : '';
  }

  get hasDeductionCodeInspection(): boolean {
    return !!this.deductionCodeInspectionProviderName;
  }

  get deductionCodeInspectionCopy(): string {
    const providerName = this.deductionCodeInspectionProviderName || 'This payroll provider';
    return `${providerName} deduction codes are available for inspection only. Payroll deduction setup, product mappings, imports, purchases, cancellations, and team writes remain disabled for this provider.`;
  }

  get showDeductionCodeNameColumn(): boolean {
    return this.deductionCodeInspectionRows.some((row) => !!row.name);
  }

  get showDeductionCodeDescriptionColumn(): boolean {
    return this.deductionCodeInspectionRows.some((row) => !!row.description);
  }

  get showDeductionCodeStatusColumn(): boolean {
    return this.deductionCodeInspectionRows.some((row) => !!row.status);
  }

  toggleDeductionCodeInspection(): void {
    this.isDeductionCodeInspectionExpanded = !this.isDeductionCodeInspectionExpanded;
  }

  ngOnDestroy(): void {
    this.meSubscription?.unsubscribe();
  }

  addConnection(): void {
    if (this.hasPayrollConnectionLimitReached) {
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: 'Only one payroll connection is supported right now. Edit or remove the current connection before adding another.',
      });
      return;
    }

    const targetCompany = this.payrollConnections[0]?.company || this.primaryCompany;
    if (!targetCompany) {
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: COMPANY_PROVISIONING_REQUIRED_MESSAGE,
      });
      return;
    }
    this.openConnectionDialog(targetCompany, 'payroll');
  }

  editPayrollConnection(row: PayrollConnectionRow): void {
    this.openConnectionDialog(row.company, 'payroll', row.providerConnection);
  }

  canRefreshHealth(row: PayrollConnectionRow): boolean {
    return row.providerSlug === PAYCOR_PROVIDER_SLUG && this.hasProviderConnection(row.providerConnection);
  }

  async refreshPaycorConnectionHealth(row: PayrollConnectionRow): Promise<void> {
    if (!this.canRefreshHealth(row) || !row.company?.id || row.isRefreshingHealth) {
      return;
    }

    const reloadId = this.reloadSequence;
    row.isRefreshingHealth = true;
    try {
      const health = await this.payrollService.refreshCompanyPayrollProviderConnectionHealth(
        row.company.id,
        row.providerSlug,
      );
      const currentRow = this.findCurrentPayrollConnectionRow(row, reloadId);
      if (!currentRow) {
        return;
      }

      currentRow.status = health?.state === 'healthy' ? 'Connected' : 'Error';
      currentRow.lastReadAt = this.getHealthLastReadAt(health) || currentRow.lastReadAt || null;
      currentRow.healthTooltip = health?.state === 'healthy'
        ? 'Read-only provider check passed.'
        : 'Unable to verify read-only provider access.';
    } catch (error: any) {
      const currentRow = this.findCurrentPayrollConnectionRow(row, reloadId);
      if (!currentRow) {
        return;
      }

      currentRow.status = 'Error';
      currentRow.lastReadAt = currentRow.lastReadAt || this.getProviderConnectionLastReadAt(currentRow.providerConnection);
      currentRow.healthTooltip = this.getProviderHealthErrorMessage(error);
    } finally {
      const currentRow = this.findCurrentPayrollConnectionRow(row, reloadId);
      if (currentRow) {
        currentRow.isRefreshingHealth = false;
      }
    }
  }

  addBankConnection(): void {
    if (this.isLoadingBankConnections) {
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: 'Bank connections are still loading.',
      });
      return;
    }

    const targetCompany = this.payrollConnections[0]?.company || this.primaryCompany;
    if (!targetCompany) {
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: COMPANY_PROVISIONING_REQUIRED_MESSAGE,
      });
      return;
    }
    if (this.bankConnections.length > 0) {
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: 'Only one bank connection is supported. Remove the current one to replace it.',
      });
      return;
    }
    this.openConnectionDialog(targetCompany, 'bank');
  }

  async removePayrollConnection(row: PayrollConnectionRow): Promise<void> {
    const ok = await this.uiAlertService.presentAlertConfirm(
      'Do you really want to disconnect this payroll connection?'
    );
    if (!ok) {
      return;
    }

    try {
      this.spinnerService.show('Removing payroll connection...');
      await this.payrollService.deactivateCompanyPayrollProviderConnection(
        row.company.id,
        row.providerSlug,
      );
      this.usersService.reloadMe();
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: 'Payroll connection removed.',
      });
      await this.reloadItems(true);
    } finally {
      this.spinnerService.hide();
    }
  }

  async removeBankConnection(row: BankConnectionRow): Promise<void> {
    const ok = await this.uiAlertService.presentAlertConfirm(
      'Do you really want to remove this bank connection?'
    );
    if (!ok) {
      return;
    }

    try {
      this.spinnerService.show('Removing bank connection...');
      await this.teamCompaniesService.deletePayrollAdminBankConnection(row.company.id);
      this.usersService.reloadMe();

      this.snackBar.openFromComponent(SnackBarComponent, {
        data: 'Bank connection removed.',
      });
      await this.reloadItems(true);
    } finally {
      this.spinnerService.hide();
    }
  }

  private openConnectionDialog(
    company?: any,
    initialTab: 'payroll' | 'bank' = 'payroll',
    providerConnection: PayrollProviderConnectionResponse | null = null,
  ): void {
    const dialogData: PayrollConnectionDialogData = {
      company,
      initialTab,
      parentCompanyId: this.parentId,
      hasExistingBankConnection: this.bankConnections.length > 0,
      providerConnection,
    };

    const ref = this.dialog.open<
      PayrollConnectionDialogComponent,
      PayrollConnectionDialogData,
      PayrollConnectionDialogResult
    >(PayrollConnectionDialogComponent, {
      width: '920px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      panelClass: 'payroll-connection-dialog-panel',
      data: dialogData,
      autoFocus: false,
    });

    ref.afterClosed().subscribe(async result => {
      if (result?.saved) {
        await this.reloadItems(true);
      }
    });
  }

  private async reloadItems(forceCompanyRefresh = false): Promise<void> {
    const reloadId = ++this.reloadSequence;
    this.isLoading = true;
    if (!this.spinnerService.visibility.value) {
      this.spinnerService.show('Loading...');
    }

    try {
      const userId = this.usersService.getCurrentUserId();
      const teams = await this.teamsService.getUserTeamsInfo(userId);
      this.userTeams = Array.isArray(teams) ? teams : [];

      this.userCompanies = await this.usersService.getTeamCompanies(forceCompanyRefresh);
      this.primaryCompany = this.userCompanies[0];
      const providerConnectionEntries = await Promise.all(
        this.userCompanies.map(async (company: any) => ({
          companyId: company?.id || '',
          connection: company?.id
            ? await this.payrollService.getCompanyPayrollProviderConnection(company.id).catch(() => null)
            : null,
        })),
      );
      const providerConnectionsByCompanyId = new Map(
        providerConnectionEntries
          .filter((entry) => !!entry.companyId && this.hasProviderConnection(entry.connection))
          .map((entry) => [entry.companyId, entry.connection]),
      );
      const companiesWithConnection = this.userCompanies.filter((company: any) =>
        providerConnectionsByCompanyId.has(company?.id));
      this.payrollConnections = await Promise.all(
        companiesWithConnection.map((company: any) =>
          this.toPayrollConnectionRow(company, providerConnectionsByCompanyId.get(company?.id) || null))
      );
      this.reloadDeductionCodeInspectionRowsInBackground(reloadId);
      this.reloadBankConnectionRowsInBackground(reloadId);
    } finally {
      this.spinnerService.hide();
      this.isLoading = false;
    }
  }

  private async toPayrollConnectionRow(
    company: any,
    providerConnection: PayrollProviderConnectionResponse | null = null,
  ): Promise<PayrollConnectionRow> {
    const ukg = company?.payrollConfig?.payroll?.ukg ?? {};
    const providerSlug = providerConnection?.providerSlug || providerConnection?.connection?.providerSlug || UKG_READY_PROVIDER_SLUG;
    const setupUnavailableReason = this.getSetupUnavailableReason(providerConnection, company);
    const validation = setupUnavailableReason
      ? { isConnected: false, tooltip: setupUnavailableReason }
      : await this.validatePayrollProviderConnection(company, providerConnection);

    return {
      company,
      providerConnection,
      providerSlug,
      name: company?.name || '--',
      type: providerConnection?.provider?.name || providerConnection?.connection?.displayName || 'UKG Ready',
      systemId: this.getProviderSystemId(providerConnection, ukg),
      status: setupUnavailableReason ? 'Unsupported' : (validation.isConnected ? 'Connected' : 'Error'),
      healthTooltip: validation.tooltip || setupUnavailableReason || '',
      connectedOn: this.toDate(
        providerConnection?.connection?.metadata?.connectedAt
        || providerConnection?.connection?.settings?.connectedAt
        || this.payrollService.getUkgConnectedAt(company)
        || company?.createdAt
        || company?.updatedAt,
      ),
      lastReadAt: validation.lastReadAt || this.getProviderConnectionLastReadAt(providerConnection),
      canManageSetup: this.canManageSetup(providerConnection),
      setupUnavailableReason,
    };
  }

  private async validatePayrollProviderConnection(
    company: any,
    providerConnection: PayrollProviderConnectionResponse | null,
  ): Promise<PayrollConnectionValidationResult> {
    const companyId = company?.id;
    if (!companyId) {
      return {
        isConnected: false,
        tooltip: 'Unable to verify the payroll provider connection.',
      };
    }

    const providerSlug = providerConnection?.providerSlug || providerConnection?.connection?.providerSlug || '';
    // Backend mode is authoritative. Configuration/access errors must not trigger a census probe.
    try {
      const context = await this.payrollService.getWorkforceBrowseContext(companyId);
      if (context.mode !== 'legacy') {
        return {
          isConnected: context.availability !== 'provider_disconnected',
          lastReadAt: this.getProviderConnectionLastReadAt(providerConnection),
          tooltip: 'Using saved payroll connection state. Workforce browsing reads the database only.',
        };
      }
    } catch (_) {
      return {isConnected: false, tooltip: 'Unable to resolve payroll browsing context.'};
    }
    const cachedUkgHealth = this.payrollService.getCachedUkgConnectionHealth(company);
    const cachedHealthState = this.payrollService.getCachedUkgConnectionState(company);
    if (providerSlug !== PAYCOR_PROVIDER_SLUG && cachedHealthState === 'healthy') {
      return {
        isConnected: true,
        lastReadAt: cachedUkgHealth?.lastSuccessAt || cachedUkgHealth?.lastCheckedAt || null,
        tooltip: 'Read-only provider check passed.',
      };
    }

    if (providerSlug === PAYCOR_PROVIDER_SLUG) {
      return this.getCachedPaycorConnectionValidation(providerConnection);
    }

    try {
      await this.payrollService.getCensusMetadataForPayrollProviderCompany(
        companyId,
        providerSlug,
      );
      return {
        isConnected: true,
        lastReadAt: new Date().toISOString(),
        tooltip: 'Read-only provider check passed.',
      };
    } catch (_) {
      return {
        isConnected: false,
        lastReadAt: cachedUkgHealth?.lastSuccessAt || cachedUkgHealth?.lastCheckedAt || null,
        tooltip: 'Unable to verify read-only provider access.',
      };
    }
  }

  private getHealthLastReadAt(health: PayrollProviderConnectionHealthCheck | null | undefined): string | null {
    return health?.dataFreshness?.lastReadAt
      || health?.lastSuccessfulReadAt
      || health?.checkedAt
      || null;
  }

  private getCachedPaycorConnectionValidation(
    providerConnection: PayrollProviderConnectionResponse | null,
  ): PayrollConnectionValidationResult {
    if (!this.hasProviderConnection(providerConnection)) {
      return {
        isConnected: false,
        tooltip: 'Unable to verify the payroll provider connection.',
      };
    }

    const health = providerConnection?.connection?.metadata?.readOnlyHealth || {};
    const state = `${health?.state || ''}`.trim().toLowerCase();
    const lastReadAt = this.getProviderConnectionLastReadAt(providerConnection);

    if (state && state !== 'healthy') {
      return {
        isConnected: false,
        lastReadAt,
        tooltip: health?.lastErrorMessage || 'Unable to verify read-only provider access.',
      };
    }

    return {
      isConnected: true,
      lastReadAt,
      tooltip: state === 'healthy'
        ? 'Read-only provider check passed.'
        : 'Using saved provider connection state; health refresh is running.',
    };
  }

  private findCurrentPayrollConnectionRow(
    row: PayrollConnectionRow,
    reloadId: number,
  ): PayrollConnectionRow | null {
    if (reloadId !== this.reloadSequence) {
      return null;
    }

    const companyId = row.company?.id || '';
    return this.payrollConnections.find((currentRow) =>
      currentRow.company?.id === companyId
      && currentRow.providerSlug === row.providerSlug
    ) || null;
  }

  private getProviderConnectionLastReadAt(
    providerConnection: PayrollProviderConnectionResponse | null | undefined,
  ): string | null {
    const health = providerConnection?.connection?.metadata?.readOnlyHealth || {};
    return health?.dataFreshness?.lastReadAt
      || health?.lastSuccessAt
      || providerConnection?.connection?.lastVerifiedAt
      || null;
  }

  private getProviderHealthErrorMessage(error: any): string {
    return error?.error?.error
      || error?.error?.message
      || error?.message
      || 'Unable to verify read-only provider access.';
  }

  private hasProviderConnection(connection: PayrollProviderConnectionResponse | null | undefined): boolean {
    return this.payrollService.hasActivePayrollProviderConnection(connection);
  }

  private canManageSetup(connection: PayrollProviderConnectionResponse | null): boolean {
    const providerSlug = connection?.providerSlug || connection?.connection?.providerSlug || '';
    return providerSlug === UKG_READY_PROVIDER_SLUG
      || providerSlug === PAYCOR_PROVIDER_SLUG
      || connection?.source === 'legacy_payroll_config'
      || connection?.isCompatibilityFallback === true;
  }

  private getSetupUnavailableReason(
    connection: PayrollProviderConnectionResponse | null,
    company: any,
  ): string {
    if (!connection) {
      return '';
    }

    const providerSlug = connection.providerSlug || connection.connection?.providerSlug || '';
    if (providerSlug === PAYCOR_PROVIDER_SLUG) {
      return '';
    }

    const capabilityReason = this.payrollService.providerCapabilityUnsupportedReason(
      connection,
      'supportsCensusMetadata',
      'connection health checks',
    );
    if (capabilityReason) {
      return capabilityReason;
    }

    if (providerSlug && providerSlug !== UKG_READY_PROVIDER_SLUG) {
      const providerName = connection.provider?.name || connection.connection?.displayName || 'This payroll provider';
      return `${providerName} setup is not available in Payroll Admin yet.`;
    }

    return '';
  }

  private getProviderSystemId(
    providerConnection: PayrollProviderConnectionResponse | null,
    ukg: any = {},
  ): string {
    const providerSlug = providerConnection?.providerSlug || providerConnection?.connection?.providerSlug || '';
    const settings = providerConnection?.connection?.settings || {};
    if (providerSlug === PAYCOR_PROVIDER_SLUG) {
      return settings.legalEntityName
        || settings.legalEntityId
        || settings.tenantId
        || '--';
    }

    return settings.companyShortName
      || ukg.companyShortName
      || '--';
  }

  private async reloadDeductionCodeInspectionRows(reloadId: number = this.reloadSequence): Promise<void> {
    const row = this.payrollConnections.find((connectionRow) => this.canInspectDeductionCodes(connectionRow));
    this.deductionCodeInspectionRows = [];
    this.deductionCodeInspectionError = '';

    if (!row?.company?.id) {
      this.deductionCodeInspectionProviderName = '';
      this.isLoadingDeductionCodes = false;
      return;
    }

    this.deductionCodeInspectionProviderName = row.type || 'Payroll provider';
    this.isLoadingDeductionCodes = true;
    try {
      const codes = await this.payrollService.getDeductionCodesForPayrollProviderCompany(
        row.company.id,
        row.providerSlug,
      );
      if (reloadId !== this.reloadSequence) {
        return;
      }

      this.deductionCodeInspectionRows = this.toDeductionCodeInspectionRows(codes);
    } catch (error: any) {
      if (reloadId !== this.reloadSequence) {
        return;
      }

      this.deductionCodeInspectionError =
        error?.error?.error || error?.error?.message || error?.message || 'Unable to load deduction codes.';
    } finally {
      if (reloadId === this.reloadSequence) {
        this.isLoadingDeductionCodes = false;
      }
    }
  }

  private reloadDeductionCodeInspectionRowsInBackground(reloadId: number): void {
    this.reloadDeductionCodeInspectionRows(reloadId)
      .then(() => {})
      .catch(() => {
        if (reloadId !== this.reloadSequence) {
          return;
        }

        this.deductionCodeInspectionError = 'Unable to load deduction codes.';
        this.isLoadingDeductionCodes = false;
      });
  }

  private reloadBankConnectionRowsInBackground(reloadId: number): void {
    this.bankConnections = [];
    this.isLoadingBankConnections = true;
    Promise.all(this.userCompanies.map((company: any) => this.getBankRowsForCompany(company)))
      .then((bankRows) => {
        if (reloadId !== this.reloadSequence) {
          return;
        }

        this.bankConnections = bankRows.reduce(
          (all: BankConnectionRow[], rows: BankConnectionRow[]) => all.concat(rows),
          []
        );
      })
      .finally(() => {
        if (reloadId !== this.reloadSequence) {
          return;
        }

        this.isLoadingBankConnections = false;
        this.maybeOpenBankDialogFromQuery();
      });
  }

  private canInspectDeductionCodes(row: PayrollConnectionRow): boolean {
    return row.providerSlug === PAYCOR_PROVIDER_SLUG
      && this.payrollService.providerConnectionSupportsCapability(
        row.providerConnection,
        'supportsDeductionCodes',
      )
      && !this.payrollService.providerConnectionSupportsCapability(
        row.providerConnection,
        'supportsPayrollDeductionSetup',
      );
  }

  private toDeductionCodeInspectionRows(codes: UkgDeductionCode[] = []): DeductionCodeInspectionRow[] {
    return (Array.isArray(codes) ? codes : [])
      .map((code: UkgDeductionCode) => ({
        code: this.toDisplayText(code?.code),
        description: this.toDisplayText(code?.description),
        name: this.toDisplayText(code?.name),
        status: this.toDisplayText(code?.status),
      }))
      .filter((row) => !!(row.code || row.name || row.description || row.status))
      .sort((left, right) => {
        const leftLabel = `${left.code} ${left.name} ${left.description}`.trim();
        const rightLabel = `${right.code} ${right.name} ${right.description}`.trim();
        return leftLabel.localeCompare(rightLabel);
      });
  }

  private toDisplayText(value: any): string {
    return value === undefined || value === null ? '' : `${value}`.trim();
  }

  private async getBankRowsForCompany(company: any): Promise<BankConnectionRow[]> {
    try {
      const companyTeams = this.filterTeamsForCompany(this.userTeams, company.id);
      const companyBankConnection = this.teamCompaniesService.getPayrollAdminBankConnection(company);
      if (companyBankConnection?.stripeCustomerId || companyBankConnection?.bankInfo?.companyId) {
        return [this.toCompanyBankConnectionRow(company, companyTeams, companyBankConnection)];
      }

      const teamMethods = await Promise.all(
        companyTeams.map(async (team: any) => ({
          team,
          methods: await this.teamsService.getPaymentMethods(team.id),
        }))
      );

      const uniqueBankRows = new Map<string, BankConnectionRow>();
      for (const teamMethodSet of teamMethods) {
        const team = teamMethodSet.team;
        const methods = teamMethodSet.methods ?? [];
        for (const method of methods) {
          const isBankMethod =
            method?.bankInfo?.type === 'us_bank_account' ||
            (!!method?.stripePaymentMethod && !!method?.bankInfo?.paymentMethodReferenceId);
          if (!isBankMethod) {
            continue;
          }

          const key =
            method?.stripePaymentMethod ||
            method?.bankInfo?.paymentMethodReferenceId ||
            method?.id;
          if (!key) {
            continue;
          }

          const existing = uniqueBankRows.get(key);
          if (!existing) {
            uniqueBankRows.set(key, {
              company,
              teamIds: [team.id],
              teamNames: [team.name || team.id],
              methodKey: key,
              name: method?.bankInfo?.bankName ? `${method.bankInfo.bankName} Account` : 'Bank Account',
              bank: method?.bankInfo?.bankName || '--',
              account: method?.stripeLast4 ? `****${method.stripeLast4}` : '--',
              fundingSource: team.name || '--',
              status: method?.bankInfo?.isPending ? 'Pending' : 'Connected',
              connectedOn: this.toDate(method?.updatedAt || method?.createdAt),
            });
            continue;
          }

          if (!existing.teamIds.includes(team.id)) {
            existing.teamIds.push(team.id);
          }
          if (!existing.teamNames.includes(team.name || team.id)) {
            existing.teamNames.push(team.name || team.id);
          }
          existing.fundingSource = existing.teamNames.join(', ');
          if (existing.status !== 'Connected' && !method?.bankInfo?.isPending) {
            existing.status = 'Connected';
          }
          const existingDate = new Date(existing.connectedOn).getTime();
          const nextDate = new Date(this.toDate(method?.updatedAt || method?.createdAt)).getTime();
          if (!Number.isNaN(nextDate) && (Number.isNaN(existingDate) || nextDate > existingDate)) {
            existing.connectedOn = this.toDate(method?.updatedAt || method?.createdAt);
          }
        }
      }

      return Array.from(uniqueBankRows.values());
    } catch {
      return [];
    }
  }

  private toCompanyBankConnectionRow(
    company: any,
    companyTeams: any[],
    connection: any,
  ): BankConnectionRow {
    const teamIds = companyTeams.map((team: any) => team?.id).filter(Boolean);
    const teamNames = companyTeams.map((team: any) => team?.name || team?.id).filter(Boolean);
    const bankName = connection?.bankInfo?.bankName || 'Bank Account';
    const methodKey =
      connection?.stripePaymentMethod ||
      connection?.bankInfo?.companyConnectionReferenceId ||
      connection?.bankInfo?.paymentMethodReferenceId ||
      '';

    return {
      company,
      teamIds,
      teamNames,
      methodKey,
      name: bankName ? `${bankName} Account` : 'Bank Account',
      bank: bankName || '--',
      account: connection?.stripeLast4 ? `****${connection.stripeLast4}` : '--',
      fundingSource: teamNames.length > 0 ? teamNames.join(', ') : 'All current and future teams',
      status: connection?.bankInfo?.isPending ? 'Pending' : 'Connected',
      connectedOn: this.toDate(connection?.connectedAt || connection?.updatedAt || company?.updatedAt || company?.createdAt),
    };
  }

  private toDate(value: any): string {
    if (!value) {
      return '--';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return '--';
    }

    return parsed.toISOString().slice(0, 10);
  }

  private filterTeamsForCompany(teams: any[], companyId: string): any[] {
    if (!Array.isArray(teams)) {
      return [];
    }
    const withCompanyId = teams.filter(team => !!team?.companyId);
    if (withCompanyId.length === 0) {
      return teams;
    }
    return teams.filter(team => team?.companyId === companyId);
  }

  private maybeOpenBankDialogFromQuery(): void {
    if (this.handledOpenBankDialogFromQuery) {
      return;
    }

    const shouldOpen = this.route.snapshot.queryParamMap.get('openBankConnectionDialog') === '1';
    if (!shouldOpen) {
      return;
    }
    if (this.isLoadingBankConnections) {
      return;
    }
    if (this.bankConnections.length > 0) {
      this.handledOpenBankDialogFromQuery = true;
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { openBankConnectionDialog: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
      return;
    }

    const targetCompany = this.payrollConnections[0]?.company || this.primaryCompany;
    if (!targetCompany) {
      return;
    }

    this.handledOpenBankDialogFromQuery = true;
    this.openConnectionDialog(targetCompany, 'bank');
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { openBankConnectionDialog: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

}
